type AmbientSettings = { master: number; wick: boolean; copal: boolean; drone: boolean };

class ProceduralAudioEngine {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private ambient: AudioNode[] = [];
  private chargeOscillator: OscillatorNode | null = null;
  private chargeGain: GainNode | null = null;

  private ensureContext() {
    this.context ||= new AudioContext();
    if (this.context.state === "suspended") void this.context.resume();
    return this.context;
  }

  start(settings: AmbientSettings) {
    this.stopAmbient();
    const context = this.ensureContext();
    const master = context.createGain();
    master.gain.value = Math.max(0, Math.min(0.55, settings.master * 0.5));
    master.connect(context.destination);
    this.master = master;

    if (settings.wick) {
      const length = context.sampleRate * 2;
      const buffer = context.createBuffer(1, length, context.sampleRate);
      const data = buffer.getChannelData(0);
      for (let index = 0; index < length; index += 1) {
        const pulse = Math.random() > 0.994 ? (Math.random() - 0.5) * 1.8 : 0;
        data[index] = (Math.random() - 0.5) * 0.08 + pulse;
      }
      const source = context.createBufferSource();
      const filter = context.createBiquadFilter();
      const gain = context.createGain();
      source.buffer = buffer; source.loop = true;
      filter.type = "bandpass"; filter.frequency.value = 2300; filter.Q.value = 0.7;
      gain.gain.value = 0.12;
      source.connect(filter).connect(gain).connect(master); source.start();
      this.ambient.push(source);
    }
    if (settings.copal) {
      const oscillator = context.createOscillator();
      const filter = context.createBiquadFilter();
      const gain = context.createGain();
      oscillator.type = "sine"; oscillator.frequency.value = 48;
      filter.type = "lowpass"; filter.frequency.value = 120;
      gain.gain.value = 0.08;
      oscillator.connect(filter).connect(gain).connect(master); oscillator.start();
      this.ambient.push(oscillator);
    }
    if (settings.drone) {
      [73.42, 110.0].forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = index ? "triangle" : "sine";
        oscillator.frequency.value = frequency;
        gain.gain.value = index ? 0.022 : 0.035;
        oscillator.connect(gain).connect(master); oscillator.start();
        this.ambient.push(oscillator);
      });
    }
  }

  update(settings: AmbientSettings) {
    if (this.master) this.master.gain.setTargetAtTime(settings.master * 0.5, this.ensureContext().currentTime, 0.08);
  }

  stopAmbient() {
    for (const node of this.ambient) {
      try { (node as AudioScheduledSourceNode).stop(); } catch { /* already stopped */ }
      try { node.disconnect(); } catch { /* noop */ }
    }
    this.ambient = [];
    try { this.master?.disconnect(); } catch { /* noop */ }
    this.master = null;
  }

  private noiseBurst(duration: number, frequency: number, volume: number) {
    const context = this.ensureContext();
    const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * duration), context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < data.length; index += 1) data[index] = (Math.random() * 2 - 1) * (1 - index / data.length);
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = buffer; filter.type = "bandpass"; filter.frequency.value = frequency;
    gain.gain.setValueAtTime(volume, context.currentTime); gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + duration);
    source.connect(filter).connect(gain).connect(context.destination); source.start();
  }

  matchStrike() { this.noiseBurst(0.32, 3000, 0.16); }
  paperBurn() { this.noiseBurst(0.9, 1500, 0.13); }
  cigarPuff() { this.noiseBurst(0.45, 520, 0.07); }

  setCharge(amount: number) {
    const context = this.ensureContext();
    if (!this.chargeOscillator) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = "sawtooth"; oscillator.frequency.value = 48; gain.gain.value = 0;
      oscillator.connect(gain).connect(context.destination); oscillator.start();
      this.chargeOscillator = oscillator; this.chargeGain = gain;
    }
    this.chargeOscillator.frequency.setTargetAtTime(48 + amount * 3.2, context.currentTime, 0.05);
    this.chargeGain?.gain.setTargetAtTime(Math.min(0.12, amount / 900), context.currentTime, 0.04);
  }

  stopCharge() {
    try { this.chargeOscillator?.stop(); } catch { /* noop */ }
    this.chargeOscillator = null;
    this.chargeGain = null;
  }

  burst() {
    this.stopCharge();
    this.noiseBurst(1.2, 800, 0.28);
    const context = this.ensureContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(620, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(42, context.currentTime + 1.1);
    gain.gain.setValueAtTime(0.18, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 1.1);
    oscillator.connect(gain).connect(context.destination); oscillator.start(); oscillator.stop(context.currentTime + 1.15);
  }
}

export const audioEngine = new ProceduralAudioEngine();
